import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

// Import individual translation files to avoid Jest conflicts
import { en } from '../Translations/Languages/en';
import { es } from '../Translations/Languages/es';
import { fr } from '../Translations/Languages/fr';
import { hi } from '../Translations/Languages/hi';
import { bn } from '../Translations/Languages/bn';
import { pt } from '../Translations/Languages/pt';
import { it } from '../Translations/Languages/it';
import { ru } from '../Translations/Languages/ru';
import { ja } from '../Translations/Languages/ja';
import { ko } from '../Translations/Languages/ko';
import type { Language, TranslationKeys } from '../Translations';

// Create translations object locally to avoid Jest type conflicts
const localTranslations = {
  en,
  es,
  fr,
  hi,
  bn,
  pt,
  it,
  ru,
  ja,
  ko,
} as const;

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: TranslationKeys;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [language, setLanguage] = useState<Language>(() => {
    // Check localStorage for saved language preference
    const savedLang = localStorage.getItem('preferredLanguage') as Language;
    return savedLang || 'en';
  });

  useEffect(() => {
    // Save language preference to localStorage whenever it changes
    localStorage.setItem('preferredLanguage', language);
  }, [language]);

  const value: LanguageContextType = {
    language,
    setLanguage,
    t: localTranslations[language]
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};